import React, { useState, useEffect, useCallback } from 'react';
import './AccountSettings.css';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../firebase/config';
import { authService } from '../services/authService';
import {
  XMarkIcon,
  UserIcon,
  MapPinIcon,
  HeartIcon,
  LifebuoyIcon,
  LockClosedIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import SupportTicketModal from './SupportTicketModal';
import OrderService, { Order } from '../services/orderService';

interface AccountSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: string;
}

interface Address {
  id: string;
  type: 'home' | 'work' | 'other';
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  isDefault: boolean;
}

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  gender: string;
  addresses: Address[];
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ isOpen, onClose, initialTab = 'profile' }) => {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState<UserData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    gender: '',
    addresses: []
  });
  const [favorites, setFavorites] = useState<any[]>([]);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const loadUserData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.uid)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        
        // Convert addresses object to array
        const addressesObj = data.addresses || {};
        const addressesArray = Object.keys(addressesObj).map(id => ({
          id,
          ...addressesObj[id]
        }));
        
        const loadedEmail = data.email || user.email || '';
        setOriginalEmail(loadedEmail);
        setUserData({
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          email: loadedEmail,
          phoneNumber: data.phone_number || '',
          dateOfBirth: data.date_of_birth || '',
          gender: data.gender || '',
          addresses: addressesArray
        });
      }
      
      // TODO: Load favorites from Firestore
      // For now, set empty array
      setFavorites([]);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && user) {
      loadUserData();
    }
  }, [isOpen, user, loadUserData]);

  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  const loadOrders = useCallback(async () => {
    if (!user) return;

    setOrdersLoading(true);
    try {
      const orderData = await OrderService.getInstance().getCustomerOrders(user.uid);
      setOrders(orderData);
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && user && activeTab === 'orders') {
      loadOrders();
    }
  }, [isOpen, user, activeTab, loadOrders]);

  const formatPrice = (amount: number) => `£${amount.toFixed(2)}`;
  const formatDate = (date: Date) => date.toLocaleString();

  const getStatusBadgeClass = (status: string) => `customer-order-status ${status}`;

  const handleSave = async () => {
    if (!user) return;

    const normalizedEmail = userData.email.trim().toLowerCase();
    const emailChanged = normalizedEmail !== originalEmail.trim().toLowerCase();

    if (emailChanged && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      alert('Please enter a valid email address.');
      return;
    }

    setSaving(true);
    try {
      const displayName = `${userData.firstName} ${userData.lastName}`.trim();
      let emailChangeMessage = '';

      if (emailChanged) {
        const result = await authService.requestEmailChange(normalizedEmail);
        emailChangeMessage =
          result.message ||
          `We sent a confirmation link to ${normalizedEmail}. Your email will update once you confirm.`;
        setUserData((prev) => ({ ...prev, email: originalEmail }));
      }

      const { error: authError } = await supabase.auth.updateUser({
        data: { display_name: displayName },
      });
      if (authError) throw authError;

      const addressesObj: Record<string, Omit<Address, 'id'>> = {};
      userData.addresses.forEach((addr) => {
        const { id, ...addressData } = addr;
        addressesObj[id] = addressData;
      });

      const profileUpdate: Record<string, unknown> = {
        first_name: userData.firstName,
        last_name: userData.lastName,
        display_name: displayName,
        phone_number: userData.phoneNumber || '',
        date_of_birth: userData.dateOfBirth || '',
        gender: userData.gender || '',
        addresses: addressesObj,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('users')
        .update(profileUpdate)
        .eq('id', user.uid);
      if (error) throw error;

      if (emailChangeMessage) {
        alert(emailChangeMessage);
      } else {
        alert('Settings saved successfully!');
      }
    } catch (error) {
      console.error('Error saving user data:', error);
      alert(error instanceof Error ? error.message : 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAddress = () => {
    const newAddress: Address = {
      id: `addr_${Date.now()}`,
      type: 'home',
      street: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      isDefault: userData.addresses.length === 0
    };
    setUserData(prev => ({
      ...prev,
      addresses: [...prev.addresses, newAddress]
    }));
  };

  const handleAddressChange = (id: string, field: keyof Address, value: any) => {
    setUserData(prev => ({
      ...prev,
      addresses: prev.addresses.map(addr =>
        addr.id === id ? { ...addr, [field]: value } : addr
      )
    }));
  };

  const handleDeleteAddress = (id: string) => {
    setUserData(prev => ({
      ...prev,
      addresses: prev.addresses.filter(addr => addr.id !== id)
    }));
  };

  const handleSetDefaultAddress = (id: string) => {
    setUserData(prev => ({
      ...prev,
      addresses: prev.addresses.map(addr => ({
        ...addr,
        isDefault: addr.id === id
      }))
    }));
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      alert('Please fill in all password fields.');
      return;
    }
    if (newPassword.length < 6) {
      alert('New password should be at least 6 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match.');
      return;
    }

    setChangingPassword(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      alert('Password updated successfully.');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to change password.');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      onClose();
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="account-settings-overlay">
      <div className="account-settings-modal">
        <div className="account-settings-header">
          <h2>Account Settings</h2>
          <button className="close-button" onClick={onClose}>
            <XMarkIcon className="close-icon" />
          </button>
        </div>

        <div className="account-settings-body">
          <div className="account-settings-tabs">
            <button
              className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <UserIcon className="tab-icon" />
              <span>Profile</span>
            </button>
            <button
              className={`tab ${activeTab === 'security' ? 'active' : ''}`}
              onClick={() => setActiveTab('security')}
            >
              <LockClosedIcon className="tab-icon" />
              <span>Security</span>
            </button>
            <button
              className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              <ShoppingBagIcon className="tab-icon" />
              <span>Orders</span>
            </button>
            <button
              className={`tab ${activeTab === 'addresses' ? 'active' : ''}`}
              onClick={() => setActiveTab('addresses')}
            >
              <MapPinIcon className="tab-icon" />
              <span>Addresses</span>
            </button>
            <button
              className={`tab ${activeTab === 'favorites' ? 'active' : ''}`}
              onClick={() => setActiveTab('favorites')}
            >
              <HeartIcon className="tab-icon" />
              <span>Favorites</span>
            </button>
            <button
              className={`tab ${activeTab === 'support' ? 'active' : ''}`}
              onClick={() => setActiveTab('support')}
            >
              <LifebuoyIcon className="tab-icon" />
              <span>Support</span>
            </button>
          </div>

          <div className="account-settings-content">
            {loading && activeTab !== 'orders' ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading your settings...</p>
              </div>
            ) : (
              <>
                {activeTab === 'orders' && (
                  <div className="settings-section">
                    <h3>My Orders</h3>
                    <p className="settings-section-description">
                      View your purchase history and order status.
                    </p>
                    {ordersLoading ? (
                      <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading your orders...</p>
                      </div>
                    ) : orders.length === 0 ? (
                      <div className="empty-state">
                        <ShoppingBagIcon className="empty-icon" />
                        <p>No orders yet</p>
                        <small>Your completed purchases will appear here.</small>
                      </div>
                    ) : (
                      <div className="customer-orders-list">
                        {orders.map((order) => (
                          <div key={order.id} className="customer-order-card">
                            <div className="customer-order-header">
                              <div>
                                <strong>Order #{order.id.slice(-8)}</strong>
                                <p className="customer-order-date">{formatDate(order.createdAt)}</p>
                              </div>
                              <div className="customer-order-meta">
                                <span className={getStatusBadgeClass(order.status)}>{order.status}</span>
                                <span className={getStatusBadgeClass(order.paymentStatus)}>
                                  {order.paymentStatus}
                                </span>
                                <span className="customer-order-total">{formatPrice(order.totalAmount)}</span>
                              </div>
                            </div>
                            <div className="customer-order-items-preview">
                              {order.items.slice(0, 3).map((item, index) => (
                                <span key={`${item.productId}-${index}`}>
                                  {item.name} x{item.quantity}
                                </span>
                              ))}
                              {order.items.length > 3 && (
                                <span>+{order.items.length - 3} more</span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="customer-order-view-btn"
                              onClick={() => setSelectedOrder(order)}
                            >
                              View details
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'profile' && (
                  <div className="settings-section">
                    <h3>Personal Information</h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>First Name</label>
                        <input
                          type="text"
                          value={userData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          placeholder="Enter your first name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Last Name</label>
                        <input
                          type="text"
                          value={userData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          placeholder="Enter your last name"
                        />
                      </div>
                      <div className="form-group full-width">
                        <label>Email Address</label>
                        <input
                          type="email"
                          value={userData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="Enter your email address"
                          autoComplete="email"
                        />
                        <small>
                          If you change your email, we will send a confirmation link to the new address.
                        </small>
                      </div>
                      <div className="form-group">
                        <label>Phone Number</label>
                        <input
                          type="tel"
                          value={userData.phoneNumber}
                          onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                          placeholder="+1 234 567 8900"
                        />
                      </div>
                      <div className="form-group">
                        <label>Date of Birth</label>
                        <input
                          type="date"
                          value={userData.dateOfBirth}
                          onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                        />
                      </div>
                      <div className="form-group full-width">
                        <label>Gender</label>
                        <select
                          value={userData.gender}
                          onChange={(e) => handleInputChange('gender', e.target.value)}
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                          <option value="prefer-not-to-say">Prefer not to say</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'security' && (
                  <div className="settings-section">
                    <h3>Change Password</h3>
                    <p className="settings-section-description">
                      Update your password to keep your account secure.
                    </p>
                    <div className="form-grid">
                      <div className="form-group full-width">
                        <label>Current password</label>
                        <input
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))
                          }
                          placeholder="Enter your current password"
                          autoComplete="current-password"
                        />
                      </div>
                      <div className="form-group">
                        <label>New password</label>
                        <input
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))
                          }
                          placeholder="At least 6 characters"
                          autoComplete="new-password"
                        />
                      </div>
                      <div className="form-group">
                        <label>Confirm new password</label>
                        <input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                          }
                          placeholder="Confirm your new password"
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="save-button security-save-button"
                      onClick={handleChangePassword}
                      disabled={changingPassword}
                    >
                      {changingPassword ? 'Updating…' : 'Update password'}
                    </button>
                  </div>
                )}

                {activeTab === 'addresses' && (
                  <div className="settings-section">
                    <div className="section-header">
                      <h3>Saved Addresses</h3>
                      <button className="add-button" onClick={handleAddAddress}>
                        + Add New Address
                      </button>
                    </div>
                    
                    {userData.addresses.length === 0 ? (
                      <div className="empty-state">
                        <MapPinIcon className="empty-icon" />
                        <p>No addresses saved yet</p>
                        <button className="secondary-button" onClick={handleAddAddress}>
                          Add Your First Address
                        </button>
                      </div>
                    ) : (
                      <div className="address-list">
                        {userData.addresses.map((address) => (
                          <div key={address.id} className="address-card">
                            <div className="address-card-header">
                              <select
                                value={address.type}
                                onChange={(e) => handleAddressChange(address.id, 'type', e.target.value)}
                                className="address-type-select"
                              >
                                <option value="home">🏠 Home</option>
                                <option value="work">💼 Work</option>
                                <option value="other">📍 Other</option>
                              </select>
                              <div className="address-card-actions">
                                {address.isDefault && <span className="default-badge">Default</span>}
                                {!address.isDefault && (
                                  <button 
                                    className="link-button"
                                    onClick={() => handleSetDefaultAddress(address.id)}
                                  >
                                    Set as Default
                                  </button>
                                )}
                                <button 
                                  className="delete-button-small"
                                  onClick={() => handleDeleteAddress(address.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                            <div className="address-form-grid">
                              <div className="form-group full-width">
                                <label>Street Address</label>
                                <input
                                  type="text"
                                  value={address.street}
                                  onChange={(e) => handleAddressChange(address.id, 'street', e.target.value)}
                                  placeholder="123 Main Street"
                                />
                              </div>
                              <div className="form-group">
                                <label>City</label>
                                <input
                                  type="text"
                                  value={address.city}
                                  onChange={(e) => handleAddressChange(address.id, 'city', e.target.value)}
                                  placeholder="City"
                                />
                              </div>
                              <div className="form-group">
                                <label>State/Province</label>
                                <input
                                  type="text"
                                  value={address.state}
                                  onChange={(e) => handleAddressChange(address.id, 'state', e.target.value)}
                                  placeholder="State"
                                />
                              </div>
                              <div className="form-group">
                                <label>Country</label>
                                <input
                                  type="text"
                                  value={address.country}
                                  onChange={(e) => handleAddressChange(address.id, 'country', e.target.value)}
                                  placeholder="Country"
                                />
                              </div>
                              <div className="form-group">
                                <label>Postal Code</label>
                                <input
                                  type="text"
                                  value={address.postalCode}
                                  onChange={(e) => handleAddressChange(address.id, 'postalCode', e.target.value)}
                                  placeholder="12345"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'favorites' && (
                  <div className="settings-section">
                    <h3>My Favorites</h3>
                    {favorites.length === 0 ? (
                      <div className="empty-state">
                        <HeartIcon className="empty-icon" />
                        <p>No favorites yet</p>
                        <small>Items you favorite will appear here</small>
                      </div>
                    ) : (
                      <div className="favorites-grid">
                        {/* TODO: Display favorite products */}
                        <p>Favorites will be displayed here</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'support' && (
                  <div className="settings-section">
                    <h3>Contact Support</h3>
                    <p style={{ marginBottom: '1rem', color: '#6B7280' }}>
                      Questions about an order, account, or a product on the marketplace? We are here to help.
                    </p>
                    <button
                      type="button"
                      className="save-button"
                      onClick={() => setShowSupportModal(true)}
                    >
                      Submit a support ticket
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="account-settings-footer">
          <button className="logout-button" onClick={handleLogout}>
            Sign Out
          </button>
          <div className="footer-right">
            <button className="cancel-button" onClick={onClose}>Cancel</button>
            {(activeTab === 'profile' || activeTab === 'addresses') && (
              <button
                className="save-button"
                onClick={handleSave}
                disabled={saving || loading}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      </div>

      {selectedOrder && (
        <div className="order-detail-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="order-detail-header">
              <h3>Order #{selectedOrder.id.slice(-8)}</h3>
              <button type="button" className="close-button" onClick={() => setSelectedOrder(null)}>
                <XMarkIcon className="close-icon" />
              </button>
            </div>
            <div className="order-detail-body">
              <p><strong>Placed:</strong> {formatDate(selectedOrder.createdAt)}</p>
              <p>
                <strong>Status:</strong>{' '}
                <span className={getStatusBadgeClass(selectedOrder.status)}>{selectedOrder.status}</span>
              </p>
              <p>
                <strong>Payment:</strong>{' '}
                <span className={getStatusBadgeClass(selectedOrder.paymentStatus)}>
                  {selectedOrder.paymentStatus}
                </span>
              </p>
              <div className="order-detail-section">
                <strong>Items</strong>
                {selectedOrder.items.map((item, index) => (
                  <div key={`${item.productId}-${index}`} className="order-detail-line">
                    <span>{item.name} x{item.quantity}</span>
                    <span>{formatPrice(item.total)}</span>
                  </div>
                ))}
              </div>
              <div className="order-detail-section">
                <strong>Vendors</strong>
                {selectedOrder.vendorOrders.map((vendorOrder) => (
                  <div key={vendorOrder.vendorId} className="order-detail-vendor">
                    <span>{vendorOrder.vendorName}</span>
                    <span>{formatPrice(vendorOrder.total)}</span>
                  </div>
                ))}
              </div>
              <div className="order-detail-total">
                <span>Total</span>
                <span>{formatPrice(selectedOrder.totalAmount)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <SupportTicketModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        type="customer_support"
      />
    </div>
  );
};

export default AccountSettings;
